'use client';

import type { ReactNode } from 'react';

import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslations } from 'use-intl';

import { Button } from '@/shared-ui';
import { ScrollArea } from '@/ui';

import type { HandsfreeOnboardingStep } from './project-usability-handsfree-onboarding.consts';
import {
  withPadding,
  getStepIcon,
  type TargetRect,
  type CardPosition,
} from './project-usability-handsfree-onboarding.helpers';
import {
  useOnboardingPosition,
  useOnboardingCardSize,
  useOnboardingStep,
  useOnboardingCardPosition,
  useOnboardingHandlers,
} from './project-usability-handsfree-onboarding.hooks';

const HANDSFREE_ONBOARDING_HIGHLIGHT_PADDING_PX = 8;

const HighlightBox = styled.div<{ rect: TargetRect }>`
  top: ${(p) => p.rect.top}px;
  left: ${(p) => p.rect.left}px;
  width: ${(p) => p.rect.width}px;
  height: ${(p) => p.rect.height}px;
`;

const CardWrapper = styled.div<{ position: CardPosition | null }>`
  ${(p) =>
    p.position !== null &&
    `
    left: ${p.position.left}px;
    top: ${p.position.top}px;
  `}
`;

const Caret = styled.div<{
  placement: HandsfreeOnboardingStep['placement'];
}>`
  ${(p) => {
    const size = '8px';
    const bg = '#111827';
    switch (p.placement) {
      case 'left':
        return `
          left: 100%;
          top: 50%;
          margin-top: -${size};
          border-width: ${size} 0 ${size} ${size};
          border-color: transparent transparent transparent ${bg};
        `;
      case 'right':
        return `
          right: 100%;
          left: auto;
          top: 50%;
          margin-top: -${size};
          border-width: ${size} ${size} ${size} 0;
          border-color: transparent ${bg} transparent transparent;
        `;
      case 'top':
        return `
          left: 50%;
          bottom: -${size};
          margin-left: -${size};
          border-width: ${size} ${size} 0 ${size};
          border-color: ${bg} transparent transparent transparent;
        `;
      case 'bottom':
        return `
          left: 50%;
          top: -${size};
          margin-left: -${size};
          border-width: 0 ${size} ${size} ${size};
          border-color: transparent transparent ${bg} transparent;
        `;
      default:
        return '';
    }
  }}
`;

interface Props {
  stepIndex: number;
  onNext: () => void;
  onEnd: () => void;
}

function renderLabelLine(text: string): ReactNode {
  const idx = text.indexOf(': ');
  if (idx === -1) return text;
  return (
    <>
      <span className='font-bold'>{text.slice(0, idx + 1)} </span>
      {text.slice(idx + 2)}
    </>
  );
}

export default function ProjectUsabilityHandsFreeOnboarding({
  stepIndex,
  onNext,
  onEnd,
}: Props) {
  const t = useTranslations('UserTest');

  const { cardRef, cardSize } = useOnboardingCardSize();
  const { step, isLastStep, stepNumber, totalSteps, hasCloseLine } =
    useOnboardingStep(stepIndex);
  const { targetRect } = useOnboardingPosition(step);
  const { cardPosition } = useOnboardingCardPosition(
    targetRect,
    cardSize,
    step
  );
  const { handlePrimary } = useOnboardingHandlers(isLastStep, onNext, onEnd);

  if (!step) return null;

  const stepText = ((): {
    title: string;
    body: string;
    closeLine?: string;
    tip: string;
  } => {
    switch (step.id) {
      case 'taskPanel':
        return {
          title: t('tour.taskPanel.title'),
          body: t('tour.taskPanel.body'),
          closeLine: t('tour.taskPanel.closeLine'),
          tip: t('tour.taskPanel.tip'),
        };
      case 'pauseTest':
        return {
          title: t('tour.pauseTest.title'),
          body: t('tour.pauseTest.body'),
          closeLine: t('tour.pauseTest.closeLine'),
          tip: t('tour.pauseTest.tip'),
        };
      case 'nextQuestion':
        return {
          title: t('tour.nextQuestion.title'),
          body: t('tour.nextQuestion.body'),
          tip: t('tour.nextQuestion.tip'),
        };
      case 'primaryWindow':
        return {
          title: t('tour.primaryWindow.title'),
          body: t('tour.primaryWindow.body'),
          tip: t('tour.primaryWindow.tip'),
        };
      case 'reportProblem':
        return {
          title: t('tour.reportProblem.title'),
          body: t('tour.reportProblem.body'),
          tip: t('tour.reportProblem.tip'),
        };
      default:
        return { title: '', body: '', tip: '' };
    }
  })();

  return (
    <dialog
      open
      className='fixed inset-0 z-100 m-0 size-full max-h-none max-w-none border-0 bg-transparent p-0'
      data-test='handsfree-onboarding-overlay'
      aria-modal='true'
      aria-label={t('tour.stepAria', {
        current: stepNumber,
        total: totalSteps,
        title: stepText.title,
      })}
    >
      <ScrollArea className='size-full overscroll-contain'>
        {!targetRect && (
          <div
            className='absolute inset-0 bg-black/60'
            aria-hidden='true'
            data-test='handsfree-onboarding-backdrop'
          />
        )}
        {targetRect && (
          <HighlightBox
            rect={withPadding(
              targetRect,
              HANDSFREE_ONBOARDING_HIGHLIGHT_PADDING_PX
            )}
            className='absolute rounded-xl border-2 border-red-500 bg-transparent [box-shadow:0_0_0_9999px_rgba(0,0,0,0.6)]'
            data-test='handsfree-onboarding-highlight'
          />
        )}
        <CardWrapper
          ref={cardRef}
          position={cardPosition}
          data-resolved-position={Boolean(cardPosition)}
          className='pointer-events-none invisible absolute z-10 w-full max-w-sm px-4 opacity-0 transition-opacity duration-200 data-[resolved-position=true]:pointer-events-auto data-[resolved-position=true]:visible data-[resolved-position=true]:opacity-100'
          data-test='handsfree-onboarding-card-wrapper'
        >
          <div className='relative rounded-3xl bg-gray-900 p-6 shadow-xl'>
            <Caret
              placement={step.placement}
              className='absolute size-0 border-solid'
              aria-hidden='true'
            />
            <div className='flex flex-col gap-4'>
              <div className='flex items-center gap-2'>
                <span
                  className='flex size-7.5 shrink-0 items-center justify-center text-white'
                  aria-hidden='true'
                >
                  <FontAwesomeIcon
                    icon={getStepIcon(step.icon)}
                    className='text-3xl'
                  />
                </span>
                <h3 className='text-lg/5 font-bold text-white'>
                  {stepText.title}
                </h3>
              </div>
              <div className='flex flex-col gap-2 text-sm/5 text-white'>
                <p>{renderLabelLine(stepText.body)}</p>
                {hasCloseLine && (
                  <p>{renderLabelLine(stepText.closeLine ?? '')}</p>
                )}
                <p>
                  <span className='font-bold'>{t('tour.tipLabel')}</span>{' '}
                  {stepText.tip}
                </p>
              </div>
              <div className='flex items-center justify-between gap-2'>
                <span className='text-sm/5 text-white' aria-hidden='true'>
                  {t('tour.pagination', {
                    current: stepNumber,
                    total: totalSteps,
                  })}
                </span>
                <div className='flex gap-2'>
                  <Button
                    variant='secondary'
                    className='rounded-[40px] border-0 bg-white px-4 py-2 text-sm font-semibold text-neutral-950'
                    onClick={handlePrimary}
                    aria-label={
                      isLastStep ? t('tour.endAria') : t('tour.nextAria')
                    }
                    data-test='handsfree-onboarding-primary'
                  >
                    {isLastStep ? t('tour.end') : t('tour.next')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </CardWrapper>
      </ScrollArea>
    </dialog>
  );
}
